import React, { useRef, useCallback } from 'react';
import { setRef } from '@shared/utils/hooks/setRef';
import { useTourStepRegistration } from './hooks/useTourStepRegistration';
import type { TourStepProps } from './Tour.types';

/**
 * TourStep - Registers a UI element as a tour step target
 *
 * Each step contains its own visual content as per the design requirements.
 * The step self-registers with the TourProvider when mounted and only registers
 * if its tourName matches the active tour.
 *
 * This component uses React.cloneElement to attach the registration ref directly
 * to the child element, avoiding extra DOM nodes. Refs are merged to preserve
 * any existing ref on the child.
 */
export const TourStep: React.FC<TourStepProps> = ({ stepName, tourName, tooltip, children }) => {
  const registrationRef = useTourStepRegistration(stepName, tourName, tooltip);

  // Validate required props in development
  if (__DEV__) {
    if (!stepName) {
      console.warn('TourStep: stepName prop is required');
    }
    if (!tourName) {
      console.warn('TourStep: tourName prop is required');
    }
    if (!tooltip) {
      console.warn('TourStep: tooltip prop is required');
    }
  }

  const child = React.Children.only(children);
  const existingRefRef = useRef<React.Ref<HTMLElement> | undefined>(undefined);

  // Read existing ref from child and store it (reading during render is safe for refs)
  const currentExistingRef = 'ref' in child ? (child as React.ReactElement & { ref?: React.Ref<HTMLElement> }).ref : undefined;
  if (currentExistingRef !== existingRefRef.current) {
    existingRefRef.current = currentExistingRef;
  }

  // Create stable merged ref callback that calls both refs
  // This callback is stable (only depends on registrationRef) and reads existingRefRef.current when called
  const mergedRef = useCallback(
    (node: HTMLElement | null) => {
      // Call registration ref
      setRef(registrationRef, node);
      // Call existing ref if present (read from ref to get latest value)
      const existingRef = existingRefRef.current;
      if (existingRef) {
        setRef(existingRef, node);
      }
    },
    [registrationRef],
  );

  // Clone the child and merge our registration ref with any existing ref
  return React.cloneElement(child, {
    ref: mergedRef,
    'data-tour-step': stepName,
    'data-tour-name': tourName,
  });
};

TourStep.displayName = 'TourStep';
